import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RevealWrapper from './RevealWrapper';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';
import defterKasaLogo from '@/pages/images/defter-kasa-logo.png';
import { getProductProgress } from '@/config/productProgress';
import { useProductProgress } from '@/context/ProductProgressContext';
import { PRODUCT_CARD_THEMES, type ProductThemeKey } from '@/config/productCardTheme';

type Tab = 'apps' | 'games';

interface AppItem {
  id: string;
  productKey: ProductThemeKey;
  nameKey: string;
  categoryKey: string;
  status: 'LIVE' | 'DEV' | 'SOON' | 'CANCELLED';
  version: string;
  descriptionKey: string;
  image: string;
  route?: string;
  externalLink?: string;
  windowsLink?: string;
  tags: string[];
  progress: number;
  progressLabelKey: string;
  cancelled?: boolean;
}

const apps: AppItem[] = [
  {
    id: '01',
    productKey: 'defter-kasa',
    nameKey: 'apps.dk.name',
    categoryKey: 'app.defterkasa.category',
    status: 'LIVE',
    version: 'a1.0.1',
    descriptionKey: 'app.defterkasa.description',
    image: defterKasaLogo,
    route: '/defter-kasa',
    externalLink: 'https://play.google.com/store/apps/details?id=com.companyname.kucukesnafcari&pcampaignid=web_share',
    tags: [
      'apps.dk.tag.offlineFirst',
      'apps.dk.tag.invoiceGeneration',
      'apps.dk.tag.expenseTracking',
      'apps.dk.tag.pdfExport',
      'apps.dk.tag.customerManagement',
      'apps.dk.tag.multiCurrency',
    ],
    progress: getProductProgress('defter-kasa'),
    progressLabelKey: 'app.defterkasa.progressLabel',
  },
  {
    id: '02',
    productKey: 'kozmik-gunluk',
    nameKey: 'apps.kg.name',
    categoryKey: 'app.kozmik.category',
    status: 'DEV',
    version: 'v0',
    descriptionKey: 'app.kozmik.description',
    image: gSoftLogoDark,
    route: '/kozmik-gunluk',
    tags: [
      'apps.kg.tag.dailyJournaling',
      'apps.kg.tag.moodTracking',
      'apps.kg.tag.cosmicThemes',
      'apps.kg.tag.encryptedEntries',
      'apps.kg.tag.streakTracking',
      'apps.kg.tag.darkUi',
    ],
    progress: getProductProgress('kozmik-gunluk'),
    progressLabelKey: 'app.kozmik.progressLabel',
  },
];

const games: AppItem[] = [
  {
    id: '01',
    productKey: 'anilar-muzesi',
    nameKey: 'games.am.name',
    categoryKey: 'games.am.category',
    status: 'CANCELLED',
    version: 'v0.0.0-archived',
    descriptionKey: 'games.am.description',
    image: gSoftLogoDark,
    route: '/anilar-muzesi',
    tags: [
      'games.am.tag.atmosphere',
      'games.am.tag.storyDriven',
      'games.am.tag.mobile',
      'games.am.tag.archive',
    ],
    progress: getProductProgress('anilar-muzesi'),
    progressLabelKey: 'games.am.progressLabel',
    cancelled: true,
  },
];

function StatusBadge({ status }: { status: AppItem['status'] }) {
  const { t } = useTranslation();
  if (status === 'LIVE') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/30 text-white bg-white/5 tracking-widest">
        {t('products.status.live', 'LIVE')}
      </span>
    );
  }
  if (status === 'DEV') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/10 text-neutral-500 tracking-widest">
        {t('products.status.dev', 'DEV')}
      </span>
    );
  }
  if (status === 'CANCELLED') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/5 text-neutral-700 tracking-widest line-through">
        {t('products.status.cancelled', 'CANCELLED')}
      </span>
    );
  }
  return (
    <span className="font-mono text-xs px-2.5 py-1 border border-white/8 text-neutral-600 tracking-widest">
      {t('products.status.soon', 'SOON')}
    </span>
  );
}

function AppRow({ item, idx }: { item: AppItem; idx: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = PRODUCT_CARD_THEMES[item.productKey];
  const liveProgress = useProductProgress(item.productKey);

  if (item.cancelled) {
    return (
      <div className="border-b border-red-900/20 bg-[#0d0808]">
        <div className="px-6 lg:px-14 py-14 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Number + Icon */}
            <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-6">
              <span className="font-black text-5xl text-red-900/20 leading-none">
                {item.id}
              </span>
              <div className={`w-16 h-16 border flex items-center justify-center grayscale ${theme.iconBox}`}>
                <img src={item.image} alt={t(item.nameKey)} className="w-12 h-12 object-contain opacity-50" />
              </div>
            </div>

            {/* Info */}
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3 className="text-2xl lg:text-3xl font-black text-neutral-600 tracking-tight line-through decoration-red-900/50">
                  {t(item.nameKey)}
                </h3>
                <StatusBadge status={item.status} />
              </div>
              <p className={`font-mono text-xs tracking-wider mb-5 ${theme.meta}`}>
                {t(item.categoryKey)} · {item.version}
              </p>
              <p className="text-neutral-700 text-sm leading-loose max-w-lg mb-8">{t(item.descriptionKey)}</p>

              {/* Cancelled Progress Bar */}
              <div className="max-w-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`font-mono text-[10px] tracking-widest uppercase ${theme.progressLabel}`}>{t('products.progress')}</span>
                  <span className={`font-mono text-[10px] tracking-widest uppercase ${theme.progressLabel}`}>{t('products.status.cancelled')}</span>
                </div>
                <div className={`h-[2px] w-full overflow-hidden ${theme.progressTrack}`}>
                  <div className={`h-full w-0 ${theme.progressFill}`} />
                </div>
                <div className="mt-1 text-right">
                  <span className={`font-mono text-[10px] ${theme.progressValue}`}>—</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="lg:col-span-3 flex lg:justify-end items-start">
              <div className="flex flex-col gap-2 w-full lg:w-auto">
                <button
                  onClick={() => navigate(item.route!)}
                  className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-red-900/30 text-red-900/60 font-mono text-xs tracking-widest uppercase hover:border-red-800/50 hover:text-red-700/80 transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-archive-line" />
                  {t('products.archive.record')}
                  <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
                </button>
                <span className="font-mono text-[10px] text-red-900/30 tracking-widest uppercase px-6">
                  {t('products.archive.notInCatalog')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-b ${theme.rowBorder} transition-colors duration-300 ${theme.rowHover} ${
        idx % 2 === 1 ? theme.rowAlt : ''
      }`}
    >
      <div className="px-6 lg:px-14 py-14 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Number + Icon */}
          <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-6">
            <span className="font-black text-5xl text-white/5 leading-none">
              {item.id}
            </span>
            <div className={`w-16 h-16 border transition-colors duration-300 flex items-center justify-center ${theme.iconBox}`}>
              <img src={item.image} alt={t(item.nameKey)} className="w-12 h-12 object-contain" />
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h3 className="text-2xl lg:text-3xl font-black text-white tracking-tight">{t(item.nameKey)}</h3>
              <StatusBadge status={item.status} />
            </div>
            <p className={`font-mono text-xs tracking-wider mb-5 ${theme.meta}`}>
              {t(item.categoryKey)} · {item.version}
            </p>
            <p className="text-neutral-400 text-sm leading-loose max-w-lg mb-8">{t(item.descriptionKey)}</p>
            <div className="flex flex-wrap gap-2 mb-8">
              {item.tags.map((tagKey) => (
                <span
                  key={tagKey}
                  className={`font-mono text-xs border px-3 py-1.5 transition-colors duration-200 ${theme.tag}`}
                >
                  {t(tagKey)}
                </span>
              ))}
            </div>

            {/* Mini Progress Bar */}
            <div className="max-w-xs">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`font-mono text-[10px] tracking-widest uppercase ${theme.progressLabel}`}>{t('products.progress')}</span>
                <span className={`font-mono text-[10px] tracking-wider ${theme.progressLabel}`}>{t(item.progressLabelKey)}</span>
              </div>
              <div className={`h-[2px] w-full overflow-hidden ${theme.progressTrack}`}>
                <div
                  className={`h-full transition-all duration-1000 ease-out ${theme.progressFill}`}
                  style={{ width: `${liveProgress}%` }}
                />
              </div>
              <div className="mt-1 text-right">
                <span className={`font-mono text-[10px] ${theme.progressValue}`}>{liveProgress}%</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="lg:col-span-3 flex lg:justify-end items-start">
            {item.route ? (
              <div className="flex flex-col gap-2 w-full lg:w-auto">
                <button
                  onClick={() => navigate(item.route!)}
                  className="group/btn inline-flex w-full justify-between items-center gap-3 px-6 py-4 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
                >
                  {t('products.viewDetails')}
                  <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
                </button>
                {item.externalLink && (
                  <a
                    href={item.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-600 font-mono text-xs tracking-widest uppercase hover:text-neutral-400 transition-colors duration-200 sm:whitespace-nowrap"
                  >
                    <i className="ri-google-play-line" />
                    {t('products.googlePlay')}
                    <i className="ri-arrow-right-up-line text-xs" />
                  </a>
                )}
                {item.windowsLink && (
                  <a
                    href={item.windowsLink}
                    className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-600 font-mono text-xs tracking-widest uppercase hover:text-neutral-400 transition-colors duration-200 sm:whitespace-nowrap"
                  >
                    <i className="ri-windows-line" />
                    {t('dk.cta.windows')}
                    <i className="ri-arrow-right-up-line text-xs" />
                  </a>
                )}
                {!item.externalLink && item.status === 'DEV' && (
                  <span className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-600 font-mono text-xs tracking-widest uppercase sm:whitespace-nowrap">
                    <i className="ri-time-line" />
                    {t('products.comingSoon')}
                  </span>
                )}
              </div>
            ) : (
              <div className="inline-flex items-center gap-3 px-6 py-4 border border-white/5 text-neutral-700 font-mono text-xs tracking-widest uppercase cursor-not-allowed sm:whitespace-nowrap">
                <i className="ri-time-line" />
                {t('products.inDevelopment')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const [activeTab, setActiveTab] = useState<Tab>('apps');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const items = activeTab === 'apps' ? apps : games;

  return (
    <section id="products" className="bg-[#0c0c0c] border-t border-white/5">
      {/* Header */}
      <RevealWrapper direction="up" threshold={0.1}>
        <div className="px-6 lg:px-14 py-10 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs text-neutral-600 tracking-widest">{t('products.label')}</span>
            <div className="flex items-center border border-white/8 p-1 rounded-full">
              <button
                onClick={() => setActiveTab('apps')}
                className={`px-4 py-1 font-mono text-xs tracking-widest uppercase rounded-full transition-all duration-200 sm:whitespace-nowrap cursor-pointer ${
                  activeTab === 'apps' ? 'bg-white text-black' : 'text-neutral-600 hover:text-neutral-400'
                }`}
              >
                {t('products.tab.apps')}
              </button>
              <button
                onClick={() => setActiveTab('games')}
                className={`px-4 py-1 font-mono text-xs tracking-widest uppercase rounded-full transition-all duration-200 sm:whitespace-nowrap cursor-pointer ${
                  activeTab === 'games' ? 'bg-white text-black' : 'text-neutral-600 hover:text-neutral-400'
                }`}
              >
                {t('products.tab.games')}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-neutral-700">{items.length} {t('products.items')}</span>
            <button
              onClick={() => navigate(activeTab === 'apps' ? '/apps' : '/games')}
              className="font-mono text-xs text-neutral-600 hover:text-neutral-300 tracking-widest uppercase transition-colors duration-200 flex items-center gap-1.5 cursor-pointer sm:whitespace-nowrap"
            >
              {t('products.viewAll')}
              <i className="ri-arrow-right-line text-xs" />
            </button>
          </div>
        </div>
      </RevealWrapper>

      {/* List */}
      {items.map((item, idx) => (
        <RevealWrapper key={`${activeTab}-${item.id}`} direction="up" delay={idx * 150} threshold={0.1}>
          <AppRow item={item} idx={idx} />
        </RevealWrapper>
      ))}
    </section>
  );
}
